import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CheckCircle2, FolderOpen, FolderSearch, Loader2, X } from 'lucide-react';
import { choosePhotoFolder, folderPermission, listFolderNames, PHOTO_FOLDER_NAME, photoFolderSupported, savedPhotoFolder } from '../../lib/claire/photoFolder';
import { ClaireService, type ClaireFolderListJob } from '../../services/claireService';
import { cn } from '../../utils/cn';
import { FileSearchCard } from './FileSearchCard';

const errText = (e: any) => e?.message?.replace(/^.*?:\s*/, '') || 'Something went wrong.';

/** Reads the folder names in the photo folder on this computer and hands them to Claire. Only names are sent, never files. */
export const FolderListCard: React.FC<{ jobId: string; compact?: boolean; onDone?: (text: string) => void }> = ({ jobId, compact, onDone }) => {
  const [job, setJob] = useState<ClaireFolderListJob | null>(null);
  const [folder, setFolder] = useState<Awaited<ReturnType<typeof savedPhotoFolder>>>(null);
  const [perm, setPerm] = useState<'granted' | 'denied' | 'prompt' | null>(null);
  const [progress, setProgress] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const started = useRef(false);

  useEffect(() => {
    let live = true;
    ClaireService.folderList('get', jobId)
      .then((j) => live && setJob(j))
      .catch((e) => live && setError(errText(e)));
    savedPhotoFolder().then(async (h) => {
      if (!live || !h) return;
      setFolder(h);
      setPerm(await folderPermission(h, false));
    });
    return () => {
      live = false;
    };
  }, [jobId]);

  const read = useCallback(
    async (root: NonNullable<typeof folder>) => {
      started.current = true;
      setError(null);
      try {
        setProgress(`Reading the folders in “${root.name}”…`);
        const names = await listFolderNames(root);
        setProgress('Sending the list to Claire…');
        const next = await ClaireService.folderList('listing', jobId, { names, folderName: root.name });
        setJob(next);
        setProgress(null);
        onDone?.(`The photo folder list is ready (${next.count ?? names.length} folders). Go ahead.`);
      } catch (e) {
        setProgress(null);
        setError(errText(e));
        started.current = false;
      }
    },
    [jobId, onDone],
  );

  useEffect(() => {
    if (job?.kind === 'list' && job.status === 'waiting' && folder && perm === 'granted' && !started.current) read(folder);
  }, [job, folder, perm, read]);

  const connect = async () => {
    setError(null);
    try {
      const h = await choosePhotoFolder();
      setFolder(h);
      setPerm('granted');
      read(h);
    } catch (e: any) {
      if (e?.name !== 'AbortError') setError(errText(e));
    }
  };

  const allow = async () => {
    if (!folder) return connect();
    const p = await folderPermission(folder, true).catch(() => 'denied' as const);
    setPerm(p);
    if (p === 'granted') read(folder);
  };

  const cancel = async () => {
    setError(null);
    try {
      setJob(await ClaireService.folderList('cancel', jobId));
    } catch (e) {
      setError(errText(e));
    }
  };

  const button = 'inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[12.5px] font-medium disabled:opacity-50';
  const primary = cn(button, 'bg-[#241813] text-white hover:bg-black');
  const secondary = cn(button, 'border border-[#e0d6c6] bg-white text-[#5a4a3a] hover:border-[#b8986e]');

  if (job?.kind === 'search') return <FileSearchCard job={job} compact={compact} onDone={onDone} />;

  return (
    <div className={cn('mt-2.5 rounded-xl border border-[#e0d3bf] bg-[#fbf7f0] px-3.5 py-3', compact ? 'text-[12.5px]' : 'text-[13.5px]')}>
      <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#9a8a78]">
        <FolderSearch className="w-3.5 h-3.5" /> Claire is looking through the photo folder
      </p>

      {!job && !error && <Loader2 className="mt-2 w-4 h-4 animate-spin text-[#9a8a78]" />}

      {job?.status === 'waiting' && !progress && (
        <div className="mt-1.5 space-y-2 text-[#241813]">
          <p>Only the folder names are shared with Claire, not the photos.</p>
          {!photoFolderSupported() ? (
            <p className="text-[12px] text-amber-800">This needs Chrome or Edge on the computer that has the photo folder.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {!folder ? (
                <button onClick={connect} className={primary}>
                  <FolderOpen className="w-3.5 h-3.5" /> Choose “{PHOTO_FOLDER_NAME}”
                </button>
              ) : perm !== 'granted' ? (
                <button onClick={allow} className={primary}>
                  <FolderOpen className="w-3.5 h-3.5" /> Allow access to “{folder.name}”
                </button>
              ) : (
                <button onClick={() => read(folder)} className={primary}>
                  <FolderSearch className="w-3.5 h-3.5" /> Read folder names
                </button>
              )}
              {folder && (
                <button onClick={connect} className={secondary}>
                  Use a different folder
                </button>
              )}
              <button onClick={cancel} className={secondary}>
                <X className="w-3.5 h-3.5" /> Cancel
              </button>
            </div>
          )}
        </div>
      )}

      {progress && (
        <p className="mt-2 flex items-center gap-2 text-[#5a4a3a]">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> {progress}
        </p>
      )}

      {job?.status === 'done' && (
        <p className="mt-1.5 flex items-center gap-1.5 text-emerald-700">
          <CheckCircle2 className="w-3.5 h-3.5" /> Sent Claire {job.count ?? 0} folder names from “{job.folderName || PHOTO_FOLDER_NAME}”.
        </p>
      )}
      {job?.status === 'cancelled' && <p className="mt-1.5 text-[#8a7a6a]">Cancelled.</p>}
      {error && <p className="mt-1.5 text-[12px] text-red-700">{error}</p>}
    </div>
  );
};
